import { NumberInput } from '@mantine/core';
import { currentYear, experienceOk, FIRST_LICENCE_YEAR_MIN } from '@/lib/driverStandards';
import { useCopy } from '@/lib/settings';

interface Props {
  value: number | null;
  onChange: (year: number | null) => void;
  /** settings.minimumYearsLicensed (the field is only shown when > 0). */
  minYears: number;
  /** The API refused the year (licence_experience_required). */
  apiError?: boolean;
  disabled?: boolean;
  /** Show "Please enter the year" when it is still empty (after the member tried to continue). */
  showEmptyError?: boolean;
}

/**
 * Year the licence was first issued. A year that is too recent doesn't just say "wrong": it says
 * when the member can start offering lifts (copy key licence.experience_wait).
 */
export function LicenceExperienceField({ value, onChange, minYears, apiError, disabled, showEmptyError }: Props) {
  const { t } = useCopy();
  const now = currentYear();
  const outOfRange = !!value && (value < FIRST_LICENCE_YEAR_MIN || value > now);
  const tooNew = !!value && !outOfRange && !experienceOk(value, minYears);
  const error = outOfRange
    ? `Please enter a year from ${FIRST_LICENCE_YEAR_MIN} to ${now}.`
    : tooNew
      ? t('licence.experience_wait', undefined, { year: value + minYears, years: minYears })
      : apiError
        ? t('licence.experience_required', undefined, { years: minYears })
        : showEmptyError && !value
          ? 'Please enter the year your licence was first issued.'
          : undefined;
  return (
    <NumberInput
      label="Year your licence was first issued"
      description="You'll find it on your licence card."
      placeholder={`For example, ${now - Math.max(minYears, 1) - 3}`}
      value={value ?? ''}
      onChange={(v) => onChange(typeof v === 'number' && Number.isFinite(v) ? v : v === '' ? null : Number(v) || null)}
      min={FIRST_LICENCE_YEAR_MIN}
      max={now}
      allowDecimal={false}
      allowNegative={false}
      hideControls
      inputMode="numeric"
      maxLength={4}
      clampBehavior="none"
      required
      disabled={disabled}
      error={error}
    />
  );
}
